/**
 * Progressive tasklist load (NXTGEN-8043 F4): flag, page plan, load generation,
 * ordered append and the page runner. No imports so node --test can load it.
 */

export const TASKLIST_LOAD_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  COMPLETE: 'complete',
  ERROR: 'error',
  CANCELLED: 'cancelled',
  // Rows are still shown, but the next getAllTasks must refetch. A failed
  // task-detail checklist reload uses this because progressive callers reuse ERROR.
  STALE: 'stale',
});

/**
 * Progressive load is on unless the build env sets it to the string 'false'.
 * @param {string|undefined} envValue VUE_APP_FC_TASKLIST_PROGRESSIVE_LOAD
 * @param {boolean} [defaultEnabled]
 * @returns {boolean}
 */
export function resolveProgressiveLoadEnabled(envValue, defaultEnabled = true) {
  if (envValue === 'false') return false;
  return defaultEnabled;
}

/**
 * getchecklist body for one progressive page. offset is the row to start at.
 * No sort and no include: the server default { project, stage, duedate, _id }
 * keeps pages stable, and the list does not ask for full info requests or raw recon.
 * @param {{ offset: number, limit: number }} entry
 */
export function checklistPageBody(entry) {
  return { pagination: { offset: entry.offset, limit: entry.limit } };
}

/**
 * getchecklist body for the first page.
 * @param {{ FIRST_PAGE_SIZE: number }} config
 */
export function firstPageBody(config) {
  return checklistPageBody({ offset: 0, limit: config.FIRST_PAGE_SIZE });
}

/**
 * Pages to fetch after the first page. Each offset starts where the previous
 * range ended, so a 100-row first page and 200-row later pages do not overlap.
 * @param {number} totalCount totalCount from the first page
 * @param {{ FIRST_PAGE_SIZE: number, PAGE_SIZE: number }} config
 * @returns {{ index: number, offset: number, limit: number }[]}
 */
export function buildRemainingPagePlan(totalCount, config) {
  const total = Math.max(0, Number(totalCount) || 0);
  const firstSize = config.FIRST_PAGE_SIZE;
  const pageSize = config.PAGE_SIZE;
  if (total <= firstSize || pageSize <= 0) return [];
  const plan = [];
  let offset = firstSize;
  while (offset < total) {
    plan.push({
      index: plan.length + 1,
      offset,
      limit: pageSize,
    });
    offset += pageSize;
  }
  return plan;
}

/**
 * True when a page response still belongs to the active load.
 * @param {{ cycleId: string, generation: number }|null} current
 * @param {{ cycleId: string, generation: number }|null} token
 */
export function isCurrentLoad(current, token) {
  if (!current || !token) return false;
  return current.generation === token.generation
    && String(current.cycleId) === String(token.cycleId);
}

function rowId(row) {
  return row && row._id != null ? String(row._id) : '';
}

/**
 * Buffer for out-of-order page arrival. Page 0 (the first page) is already in the list.
 * @param {object[]} firstRows rows already committed
 */
export function createPageBuffer(firstRows = []) {
  const seenIds = new Set();
  (Array.isArray(firstRows) ? firstRows : []).forEach((row) => {
    const id = rowId(row);
    if (id) seenIds.add(id);
  });
  return {
    nextIndex: 1,
    pending: new Map(),
    completed: new Set([0]),
    seenIds,
  };
}

/**
 * Store one page and return the rows that can be appended now, in page order.
 * A page that arrives before the gap in front of it is filled waits in the buffer.
 * Rows whose id is already in the list are dropped.
 * @returns {object[]}
 */
export function addPageToBuffer(buffer, index, rows) {
  if (index < buffer.nextIndex || buffer.pending.has(index)) return [];
  buffer.pending.set(index, Array.isArray(rows) ? rows : []);
  buffer.completed.add(index);
  const ready = [];
  while (buffer.pending.has(buffer.nextIndex)) {
    const pageRows = buffer.pending.get(buffer.nextIndex);
    buffer.pending.delete(buffer.nextIndex);
    buffer.nextIndex += 1;
    pageRows.forEach((row) => {
      const id = rowId(row);
      if (id && buffer.seenIds.has(id)) return;
      if (id) buffer.seenIds.add(id);
      ready.push(row);
    });
  }
  return ready;
}

/** Plan entries not yet received (flushed or buffered). */
export function missingPages(plan, buffer) {
  return (Array.isArray(plan) ? plan : []).filter((entry) => !buffer.completed.has(entry.index));
}

/**
 * Fetch pages with a fixed number of workers. After the first failure no new
 * page is started; pages already in flight may still finish and are passed to onPage.
 * @param {object} options
 * @param {object[]} options.pages
 * @param {number} options.concurrency
 * @param {(entry: object) => Promise<*>} options.fetchPage
 * @param {(entry: object, result: *) => void} options.onPage
 * @param {() => boolean} options.isCurrent
 * @returns {Promise<{ status: 'complete'|'failed'|'stale', failedPage?: object, error?: * }>}
 */
export async function runPagePlan({ pages, concurrency, fetchPage, onPage, isCurrent }) {
  const queue = Array.isArray(pages) ? pages.slice() : [];
  let failure = null;

  const worker = async () => {
    while (!failure && queue.length && isCurrent()) {
      const entry = queue.shift();
      let result;
      try {
        result = await fetchPage(entry);
      } catch (error) {
        if (!failure) failure = { failedPage: entry, error };
        return;
      }
      if (!isCurrent()) return;
      onPage(entry, result);
    }
  };

  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, queue.length || 1));
  await Promise.all(Array.from({ length: workerCount }, worker));

  if (!isCurrent()) return { status: 'stale' };
  if (failure) return { status: 'failed', ...failure };
  return { status: 'complete' };
}

/**
 * Whether getAllTasks may answer from the stored list for this cycle.
 * A partial list counts for the tasklist page (it shows progress or Retry);
 * callers that need every row reload after an error or a cancelled load.
 */
export function isTasklistCacheUsable(status, { progressive = false } = {}) {
  if (status === TASKLIST_LOAD_STATUS.COMPLETE || status === TASKLIST_LOAD_STATUS.LOADING) return true;
  if (status === TASKLIST_LOAD_STATUS.ERROR) return progressive;
  return false;
}

/**
 * Task detail route for a cycle checklist (`.../checklist/:projectId/task/:taskId`).
 * @param {{ path?: string }|null|undefined} route
 */
export function isTaskDetailRoute(route) {
  const path = route?.path || '';
  return path.includes('/financialclose/project/checklist/') && path.includes('/task/');
}

/**
 * Abort the progressive tasklist load on leave, except when opening task detail
 * for the same cycle. Client switch, leaving Financial Close, and other cycle
 * pages still stop the load so a later visit can refetch.
 * @param {{ path?: string, params?: { id?: string, projectId?: string } }|null} toRoute
 * @param {{ path?: string, params?: { id?: string, projectId?: string } }|null} fromRoute
 */
export function shouldCancelTasklistOnLeave(toRoute, fromRoute) {
  if (!isTaskDetailRoute(toRoute)) return true;
  const fromCycleId = fromRoute?.params?.id;
  const toCycleId = toRoute?.params?.projectId || toRoute?.params?.id;
  if (fromCycleId == null || toCycleId == null || fromCycleId === '' || toCycleId === '') return true;
  return String(fromCycleId) !== String(toCycleId);
}

/**
 * Whether getAllTasks should hit the network.
 * Back from task detail reuses stored rows (including a partial list still loading).
 * Explicit refresh, a client switch (no stored list for this cycle), and a load
 * cancelled by leaving Financial Close refetch.
 * @param {{ forceRefresh?: boolean, sameCycle?: boolean, hasStoredList?: boolean, status?: string, progressive?: boolean }} options
 */
export function shouldRefetchTasklist({
  forceRefresh = false,
  sameCycle = false,
  hasStoredList = false,
  status,
  progressive = false,
} = {}) {
  if (forceRefresh) return true;
  if (!sameCycle || !hasStoredList) return true;
  return !isTasklistCacheUsable(status, { progressive });
}
